import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { tokens } from '../../theme/tokens';

export interface ToneSegmentOption<T extends string> {
  value: T;
  label: string;
  /** Ink of the active segment: on reads positive, off reads problem, anything else primary. */
  tone?: 'positive' | 'problem';
}

interface ToneSegmentedProps<T extends string> {
  options: Array<ToneSegmentOption<T>>;
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  disabled?: boolean;
}

const activeInk = (tone: ToneSegmentOption<string>['tone']) =>
  tone === 'positive' ? tokens.signal.positive.ink : tone === 'problem' ? tokens.signal.problem.ink : tokens.ink.primary;

/**
 * A saved one-of-n choice on a tinted track (Default · On · Off), the active
 * segment white and inked by what it means. The view-filter twin with counts is
 * `SegmentedControl` in components/mapping/v2/v2Ui.
 */
export function ToneSegmented<T extends string>({ options, value, onChange, disabled = false, ...rest }: ToneSegmentedProps<T>) {
  return (
    <Box
      role="radiogroup"
      aria-label={rest['aria-label']}
      aria-disabled={disabled || undefined}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '3px',
        p: '3px',
        width: 'fit-content',
        maxWidth: '100%',
        bgcolor: tokens.surface.tint,
        borderRadius: '9px',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => {
              if (!active) onChange(option.value);
            }}
            sx={{
              height: 26,
              px: 1.25,
              borderRadius: '7px',
              fontSize: 12.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: active ? activeInk(option.tone) : tokens.ink.muted,
              bgcolor: active ? tokens.surface.card : 'transparent',
              boxShadow: active ? tokens.shadow.card : 'none',
              '&:focus-visible': { outline: `2px solid ${tokens.accent.main}`, outlineOffset: 1 },
            }}
          >
            {option.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
